/**
 * @file CategoryFormScreen.tsx
 *
 * Routed wrapper around {@link CategoryForm} for the `/categories/new` and
 * `/categories/:id/edit` routes. Resolves the seed category for edit via the
 * store (cache-first, then fetch-by-id so a direct link or refresh still works),
 * shows loading/error states, and owns persistence: it calls the store's
 * create/update, toasts the save outcome, and navigates back to the list on
 * success. The full category list (for the parent select) is fetched on mount.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { useCategoriesStore } from '../../stores/categories.store';
import type { ShopCategoryPayload } from '../../api/shop-types';
import { EmptyState } from '../../components/EmptyState';
import { CategoryForm, type CategoryFormMode } from './CategoryForm';

interface CategoryFormScreenProps {
  mode: CategoryFormMode;
}

/**
 * Renders the routed category form for the given mode.
 *
 * @param props - The form mode (create/edit).
 *
 * @returns The category form screen (with loading/error fallbacks).
 */
export function CategoryFormScreen({ mode }: CategoryFormScreenProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();

  const categories = useCategoriesStore((s) => s.categories);
  const current = useCategoriesStore((s) => s.current);
  const formLoading = useCategoriesStore((s) => s.formLoading);
  const formError = useCategoriesStore((s) => s.formError);
  const loadOne = useCategoriesStore((s) => s.loadOne);
  const fetchCategories = useCategoriesStore((s) => s.fetchCategories);
  const submitCreate = useCategoriesStore((s) => s.submitCreate);
  const submitUpdate = useCategoriesStore((s) => s.submitUpdate);
  const resetForm = useCategoriesStore((s) => s.resetForm);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [serverErrorCode, setServerErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories();
    }
    if (mode === 'create') {
      resetForm();
    } else if (id !== undefined) {
      loadOne(id);
    }
    return () => resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  const handleSubmit = async (payload: ShopCategoryPayload): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    setServerErrorCode(null);
    const result =
      mode === 'edit' && id !== undefined
        ? await submitUpdate(id, payload)
        : await submitCreate(payload);
    setSaving(false);
    if (!result) {
      notifyError(intl.formatMessage(adminMessages.toastError));
      const code = useCategoriesStore.getState().formErrorCode;
      if (code === 'category_slug_conflict') {
        setServerErrorCode(code);
      } else {
        setSaveError(intl.formatMessage(adminMessages.categoriesFormSaveError));
      }
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastSaved));
    await fetchCategories();
    navigate('/categories');
  };

  const goBack = (): void => {
    navigate('/categories');
  };

  if (mode === 'edit') {
    if (formError) {
      return (
        <EmptyState
          title={intl.formatMessage(adminMessages.categoriesFormLoadErrorTitle)}
          description={intl.formatMessage(adminMessages.categoriesFormLoadErrorDescription)}
          action={{
            label: intl.formatMessage(adminMessages.categoriesTitle),
            onClick: goBack,
          }}
        />
      );
    }
    if (formLoading || !current || current.id !== id) {
      return (
        <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
          <Loader>
            <FormattedMessage {...adminMessages.categoriesFormLoading} />
          </Loader>
        </div>
      );
    }
  }

  return (
    <CategoryForm
      mode={mode}
      category={mode === 'create' ? undefined : (current ?? undefined)}
      categories={categories}
      saving={saving}
      saveError={saveError}
      serverErrorCode={serverErrorCode}
      onClearServerError={() => setServerErrorCode(null)}
      onSubmit={handleSubmit}
      onCancel={goBack}
    />
  );
}
